import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from '@mui/material';
import { Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';
import { Library, CreateLibraryRequest } from '@/types/api';

interface LibraryFormProps {
  open: boolean;
  library?: Library | null;
  onClose: () => void;
  onSave: (data: CreateLibraryRequest) => void;
  isLoading?: boolean;
}

const LibraryForm: React.FC<LibraryFormProps> = ({
  open,
  library,
  onClose,
  onSave,
  isLoading = false,
}) => {
  const { t } = useTranslation('components');
  const [canonicalName, setCanonicalName] = useState('');
  const [slug, setSlug] = useState('');
  const [notes, setNotes] = useState('');

  const isEditMode = !!library;

  // Reset form when dialog opens/closes or library changes
  useEffect(() => {
    if (open) {
      if (library) {
        setCanonicalName(library.canonical_name);
        setSlug(library.slug);
        setNotes(library.notes || '');
      } else {
        setCanonicalName('');
        setSlug('');
        setNotes('');
      }
    }
  }, [open, library]);

  const handleSave = () => {
    if (!canonicalName.trim() || !slug.trim()) {
      alert(t('libraryForm.validationErrors.requiredFields'));
      return;
    }

    // Validate slug format (lowercase, alphanumeric, dashes only)
    const slugRegex = /^[a-z0-9-]+$/;
    if (!slugRegex.test(slug)) {
      alert(t('libraryForm.validationErrors.invalidSlug'));
      return;
    }

    onSave({
      canonical_name: canonicalName.trim(),
      slug: slug.trim().toLowerCase(),
      notes: notes.trim() || undefined,
    });
  };

  const handleCanonicalNameChange = (value: string) => {
    setCanonicalName(value);

    // Auto-generate slug from canonical name if not editing
    if (!isEditMode && !slug) {
      const autoSlug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setSlug(autoSlug);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditMode ? t('libraryForm.editTitle') : t('libraryForm.createTitle')}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label={t('libraryForm.canonicalNameLabel')}
            fullWidth
            required
            value={canonicalName}
            onChange={(e) => handleCanonicalNameChange(e.target.value)}
            disabled={isLoading}
            inputProps={{ dir: 'rtl' }}
            helperText={t('libraryForm.canonicalNameHelper')}
          />

          <TextField
            label={t('libraryForm.slugLabel')}
            fullWidth
            required
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            disabled={isLoading}
            helperText={t('libraryForm.slugHelper')}
            placeholder={t('libraryForm.slugPlaceholder')}
          />

          <TextField
            label={t('libraryForm.notesLabel')}
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isLoading}
            helperText={t('libraryForm.notesHelper')}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          startIcon={<CloseIcon />}
          disabled={isLoading}
        >
          {t('libraryForm.actions.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          startIcon={<SaveIcon />}
          variant="contained"
          disabled={isLoading || !canonicalName.trim() || !slug.trim()}
        >
          {isLoading ? t('libraryForm.actions.saving') : t('libraryForm.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LibraryForm;
